import { useEffect, useState } from 'react';
import { Minus, Plus, Printer, Search, Trash2 } from 'lucide-react';
import { P } from '@stockpilot/shared';
import { useFormat } from '@/hooks/useAccess';
import {
  useCurrentPosSessionQuery, useHeldCartsQuery, useHoldCartMutation,
  useOpenPosSessionMutation, usePaymentMethodsQuery, usePosCheckoutMutation,
  usePosSessionActionMutation, useProductsQuery, useReleaseHeldCartMutation,
  type Sale,
} from '@/api/endpoints';
import { apiErrorMessage } from '@/api/baseApi';
import { formatMoney } from '@/lib/format';
import {
  Alert, Badge, Button, Card, CardHeader, EmptyState, Input, LoadingState, Modal,
  PageHeader, Select,
} from '@/components/ui';
import { Can } from '@/components/Can';
import { CustomerPicker } from '@/components/CustomerPicker';
import { SaleReceipt } from '@/components/SaleReceipt';

interface CartLine {
  variantId: string;
  label: string;
  price: number;
  quantity: number;
}

export function PosPage() {
  const format = useFormat();

  const session = useCurrentPosSessionQuery();
  const [search, setSearch] = useState('');
  // The server does the searching, so every product can be found, not just the
  // first page. The query waits for typing to pause rather than firing per key.
  const [productQuery, setProductQuery] = useState('');
  useEffect(() => {
    const timer = window.setTimeout(() => setProductQuery(search.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [search]);
  const { data: products, isFetching: searching } = useProductsQuery({
    limit: 12,
    status: 'ACTIVE',
    search: productQuery || undefined,
    // A till only rings up what is on the shelf.
    inStock: true,
  });
  const { data: methods } = usePaymentMethodsQuery();
  const held = useHeldCartsQuery(undefined, { skip: !session.data });

  const [openSession, openState] = useOpenPosSessionMutation();
  const [sessionAction, sessionActionState] = usePosSessionActionMutation();
  const [checkout, checkoutState] = usePosCheckoutMutation();
  const [holdCart] = useHoldCartMutation();
  const [releaseCart] = useReleaseHeldCartMutation();

  const [cart, setCart] = useState<CartLine[]>([]);
  const [customerId, setCustomerId] = useState('');
  const [failure, setFailure] = useState<string | null>(null);
  const [change, setChange] = useState<string | null>(null);
  // The sale just rung up, kept so the cashier can hand over a receipt. It is
  // the sale the API returned, not a rebuild of the cart that has been cleared.
  const [lastSale, setLastSale] = useState<Sale | null>(null);
  const [printing, setPrinting] = useState(false);

  const [opening, setOpening] = useState(false);
  const [float, setFloat] = useState('');
  const [registerName, setRegisterName] = useState('Main Register');

  const [closing, setClosing] = useState(false);
  const [counted, setCounted] = useState('');

  const [tenderMethodId, setTenderMethodId] = useState('');
  const [tendered, setTendered] = useState('');
  const [holdRef, setHoldRef] = useState('');

  const variantOptions = (products?.items ?? []).flatMap((product) =>
    (product.variants ?? []).map((variant) => ({
      id: variant.id,
      label: `${product.name} — ${variant.sku}`,
      price: Number(variant.sellingPrice ?? product.sellingPrice),
      available: variant.available ?? null,
    })),
  );

  const total = cart.reduce((sum, line) => sum + line.price * line.quantity, 0);
  const changeDue = tendered ? Number(tendered) - total : 0;

  const addToCart = (option: { id: string; label: string; price: number }): void => {
    setCart((current) => {
      const existing = current.find((line) => line.variantId === option.id);
      if (existing) {
        return current.map((line) =>
          line.variantId === option.id ? { ...line, quantity: line.quantity + 1 } : line,
        );
      }
      return [...current, { variantId: option.id, label: option.label, price: option.price, quantity: 1 }];
    });
  };

  const setQuantity = (variantId: string, quantity: number): void => {
    setCart((current) =>
      quantity <= 0
        ? current.filter((line) => line.variantId !== variantId)
        : current.map((line) => (line.variantId === variantId ? { ...line, quantity } : line)),
    );
  };

  const clearCart = (): void => {
    setCart([]);
    setTendered('');
    setCustomerId('');
  };

  const ringUp = async (): Promise<void> => {
    setFailure(null);
    setChange(null);
    try {
      const result = await checkout({
        customerId: customerId || undefined,
        lines: cart.map((line) => ({ variantId: line.variantId, quantity: line.quantity })),
        tenders: [{ methodId: tenderMethodId, amount: Number(tendered) }],
      }).unwrap();

      setChange(result.changeDue);
      setLastSale(result.sale);
      clearCart();
    } catch (checkoutError) {
      // Short stock and short tender both come back with a usable message.
      setFailure(apiErrorMessage(checkoutError, 'That sale could not be completed'));
    }
  };

  const park = async (): Promise<void> => {
    setFailure(null);
    try {
      await holdCart({
        reference: holdRef,
        customerId: customerId || undefined,
        itemCount: cart.length,
        total,
        snapshot: { lines: cart, customerId },
      }).unwrap();
      setHoldRef('');
      clearCart();
    } catch (holdError) {
      setFailure(apiErrorMessage(holdError, 'That cart could not be parked'));
    }
  };

  const resume = async (cartId: string, snapshot: Record<string, unknown>): Promise<void> => {
    const lines = (snapshot.lines as CartLine[] | undefined) ?? [];
    setCart(lines);
    setCustomerId((snapshot.customerId as string | undefined) ?? '');
    await releaseCart(cartId).unwrap().catch(() => undefined);
  };

  if (session.isLoading) return <LoadingState />;

  // ---------------------------------------------------------- no open register
  if (!session.data) {
    return (
      <>
        <PageHeader title="Point of sale" description="Open a register to start selling." />
        <Card>
          <EmptyState
            icon={Search}
            title="No register open"
            description="A register holds the day's takings, so one has to be open before you can sell."
          />
          <div className="flex justify-center pb-6">
            <Can permission={P.POS_SESSION_OPEN}>
              <Button onClick={() => setOpening(true)}>Open a register</Button>
            </Can>
          </div>
        </Card>

        <Modal
          open={opening}
          onClose={() => setOpening(false)}
          title="Open a register"
          footer={
            <>
              <Button variant="outline" onClick={() => setOpening(false)}>Cancel</Button>
              <Button
                loading={openState.isLoading}
                onClick={async () => {
                  setFailure(null);
                  try {
                    await openSession({
                      registerName,
                      openingFloat: float ? Number(float) : 0,
                    }).unwrap();
                    setOpening(false);
                  } catch (openError) {
                    setFailure(apiErrorMessage(openError, 'That register could not be opened'));
                  }
                }}
              >
                Open register
              </Button>
            </>
          }
        >
          <div className="space-y-4">
            {failure && <Alert tone="danger">{failure}</Alert>}
            <Input
              label="Register name"
              value={registerName}
              onChange={(event) => setRegisterName(event.target.value)}
            />
            <Input
              label="Opening float"
              type="number"
              min="0"
              step="0.01"
              value={float}
              onChange={(event) => setFloat(event.target.value)}
              hint="Cash already in the drawer. It is counted back at closing."
            />
          </div>
        </Modal>
      </>
    );
  }

  // ------------------------------------------------------------- the till
  return (
    <>
      <PageHeader
        title={session.data.registerName}
        description={`${session.data.saleCount} sales · ${formatMoney(session.data.expectedCash, format)} expected in the drawer`}
        action={
          <Can permission={P.POS_SESSION_OPEN}>
            <Button variant="outline" onClick={() => setClosing(true)}>Close register</Button>
          </Can>
        }
      />

      {failure && (
        <div className="mb-4">
          <Alert tone="danger">{failure}</Alert>
        </div>
      )}
      {change !== null && (
        <div className="mb-4">
          <Alert tone="success">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <span>
                Sale {lastSale?.number} complete. Change due{' '}
                <strong>{formatMoney(change, format)}</strong>.
              </span>
              {lastSale && (
                <Button size="sm" variant="outline" icon={Printer} onClick={() => setPrinting(true)}>
                  Print receipt
                </Button>
              )}
            </div>
          </Alert>
        </div>
      )}

      {printing && lastSale && (
        <SaleReceipt sale={lastSale} onClose={() => setPrinting(false)} />
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">
        <Card>
          <div className="border-b border-subtle p-4">
            <Input
              label="Find a product"
              icon={Search}
              placeholder="Name or SKU"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>

          <div className="grid gap-2 p-4 sm:grid-cols-2 xl:grid-cols-3">
            {variantOptions.map((option) => (
              <button
                key={option.id}
                type="button"
                className="rounded-lg border border-subtle p-3 text-left hover:bg-sunken"
                // A tile that only reads out the product name does not say what
                // pressing it does.
                aria-label={`Add ${option.label}`}
                onClick={() => addToCart(option)}
              >
                <p className="text-sm font-medium">{option.label}</p>
                <p className="text-xs text-muted">
                  {formatMoney(option.price.toFixed(2), format)}
                  {option.available != null && ` · ${Number(option.available).toLocaleString()} in stock`}
                </p>
              </button>
            ))}
            {variantOptions.length === 0 && !searching && (
              <p className="text-sm text-muted">Nothing matches that search.</p>
            )}
          </div>

          {held.data && held.data.length > 0 && (
            <div className="border-t border-subtle p-4">
              <p className="mb-2 text-sm font-medium">Parked carts</p>
              <div className="flex flex-wrap gap-2">
                {held.data.map((cartEntry) => (
                  <Button
                    key={cartEntry.id}
                    size="sm"
                    variant="outline"
                    onClick={() => void resume(cartEntry.id, cartEntry.snapshot)}
                  >
                    {cartEntry.reference} · {formatMoney(cartEntry.total, format)}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card>
          <CardHeader title="Cart" />

          <div className="space-y-2 p-4">
            {cart.length === 0 && <p className="text-sm text-muted">Nothing in the cart yet.</p>}

            {cart.map((line) => (
              <div key={line.variantId} className="flex items-center gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{line.label}</p>
                  <p className="text-xs text-muted">
                    {formatMoney((line.price * line.quantity).toFixed(2), format)}
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Minus}
                  onClick={() => setQuantity(line.variantId, line.quantity - 1)}
                  aria-label={`One fewer ${line.label}`}
                />
                <span className="w-6 text-center text-sm">{line.quantity}</span>
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Plus}
                  onClick={() => setQuantity(line.variantId, line.quantity + 1)}
                  aria-label={`One more ${line.label}`}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  icon={Trash2}
                  onClick={() => setQuantity(line.variantId, 0)}
                  aria-label={`Remove ${line.label}`}
                />
              </div>
            ))}
          </div>

          <div className="space-y-3 border-t border-subtle p-4">
            <div className="flex justify-between text-lg font-semibold">
              <span>Total</span>
              <span>{formatMoney(total.toFixed(2), format)}</span>
            </div>

            {/* A new customer most often turns up at the counter, so they can
                be added without leaving the till. */}
            <CustomerPicker
              value={customerId}
              onChange={setCustomerId}
            />

            <Select
              label="Tender"
              value={tenderMethodId}
              onChange={(event) => setTenderMethodId(event.target.value)}
            >
              <option value="">Select a method</option>
              {methods?.map((method) => (
                <option key={method.id} value={method.id}>{method.name}</option>
              ))}
            </Select>

            <Input
              label="Amount tendered"
              type="number"
              min="0"
              step="0.01"
              value={tendered}
              onChange={(event) => setTendered(event.target.value)}
            />

            {tendered !== '' && changeDue >= 0 && cart.length > 0 && (
              <p className="text-sm text-secondary">
                Change due{' '}
                <span className="font-semibold">{formatMoney(changeDue.toFixed(2), format)}</span>
              </p>
            )}

            <Button
              className="w-full"
              loading={checkoutState.isLoading}
              disabled={cart.length === 0 || !tenderMethodId || changeDue < 0}
              onClick={() => void ringUp()}
            >
              {changeDue < 0 && tendered !== '' ? 'Not enough tendered' : 'Complete sale'}
            </Button>

            <div className="flex gap-2">
              <Input
                placeholder="Park as…"
                value={holdRef}
                onChange={(event) => setHoldRef(event.target.value)}
              />
              <Button
                variant="outline"
                disabled={cart.length === 0 || !holdRef}
                onClick={() => void park()}
              >
                Park
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Modal
        open={closing}
        onClose={() => setClosing(false)}
        title="Close the register"
        description="Count the drawer and enter what is actually there."
        footer={
          <>
            <Button variant="outline" onClick={() => setClosing(false)}>Cancel</Button>
            <Button
              loading={sessionActionState.isLoading}
              disabled={counted === ''}
              onClick={async () => {
                setFailure(null);
                try {
                  await sessionAction({
                    id: session.data!.id,
                    action: 'close',
                    body: { countedCash: Number(counted) },
                  }).unwrap();
                  setClosing(false);
                  setCounted('');
                } catch (closeError) {
                  setFailure(apiErrorMessage(closeError, 'That register could not be closed'));
                }
              }}
            >
              Close register
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex justify-between text-sm">
            <span className="text-muted">Expected in the drawer</span>
            <span className="font-semibold">
              {formatMoney(session.data.expectedCash, format)}
            </span>
          </div>

          <Input
            label="Counted cash"
            type="number"
            min="0"
            step="0.01"
            value={counted}
            onChange={(event) => setCounted(event.target.value)}
          />

          {counted !== '' && (
            <Alert tone={Number(counted) === Number(session.data.expectedCash) ? 'success' : 'warning'}>
              {Number(counted) === Number(session.data.expectedCash)
                ? 'The drawer balances.'
                : `Variance of ${formatMoney(
                    (Number(counted) - Number(session.data.expectedCash)).toFixed(2),
                    format,
                  )}. It is recorded either way.`}
            </Alert>
          )}
        </div>
      </Modal>
    </>
  );
}

/** Exported for the sessions list on the reports side. */
export function SessionBadge({ status }: { status: string }) {
  const tone = status === 'OPEN' ? 'success' : status === 'CLOSED' ? 'warning' : 'neutral';
  return <Badge tone={tone}>{status.toLowerCase()}</Badge>;
}
